// src/api/blog.api.js
import API_BASE from "./api";

export class BlogService {

    // Get all published blogs with pagination & filters
    static async getAllBlogs(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetch(`${API_BASE}/blogs?${query}`, {
            method: 'GET',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch blogs');
        }
        return data;
    }
    static async getAllAdminBlogs(params = {}) {
        const query = new URLSearchParams(params).toString();
        const response = await fetch(`${API_BASE}/blogs/admin/all?${query}`, {
            method: 'GET',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch blogs');
        }
        return data;
    }


    // Get blog by slug (public view)
    static async getBlogBySlug(slug) {
        const response = await fetch(`${API_BASE}/blogs/${slug}`, {
            method: 'GET',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch blog');
        }
        return data;
    }

    // Create a new blog post
    static async createBlog(blogData) {
        const response = await fetch(`${API_BASE}/blogs`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(blogData)
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to create blog');
        }
        return data;
    }

    // Update an existing blog post
    static async updateBlog(id, blogData) {
        const response = await fetch(`${API_BASE}/blogs/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(blogData)
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to update blog');
        }
        return data;
    }

    // Delete a blog post
    static async deleteBlog(id) {
        const response = await fetch(`${API_BASE}/blogs/${id}`, {
            method: 'DELETE',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to delete blog');
        }
        return data;
    }

    // Upload image to ImageKit
    static async uploadImage(file) {
        const formData = new FormData();
        formData.append("image", file);

        const response = await fetch("https://api.kromaic.store/api/upload", {
            method: "POST",
            body: formData,
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to upload image');
        }
        return data.url;
    }

    // Search blogs
    static async searchBlogs(query, params = {}) {
        const searchParams = new URLSearchParams({
            q: query,
            ...params
        }).toString();

        const response = await fetch(`${API_BASE}/blogs/search?${searchParams}`, {
            method: 'GET',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Blog search failed');
        }
        return data;
    }

    // Get related blogs
    static async getRelatedPosts(slug, limit = 4) {
        const response = await fetch(
            `${API_BASE}/blogs/related/${slug}?limit=${limit}`,
            {
                method: 'GET',
                credentials: 'include'
            }
        );

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch related posts');
        }
        return data;
    }

    // Get popular blogs
    static async getPopularBlogs(limit = 10) {
        const response = await fetch(
            `${API_BASE}/blogs/popular?limit=${limit}`,
            {
                method: 'GET',
                credentials: 'include'
            }
        );

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch popular blogs');
        }
        return data;
    }

    // Get blog categories with counts
    static async getBlogCategories() {
        const response = await fetch(`${API_BASE}/blogs/categories`, {
            method: 'GET',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch categories');
        }
        return data;
    }

    // Get tags cloud
    static async getTagsCloud() {
        const response = await fetch(`${API_BASE}/blogs/tags`, {
            method: 'GET',
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to fetch tags');
        }
        return data;
    }

    // Increment blog views (analytics only)
    static async incrementBlogViews(blogId) {
        try {
            await fetch(`${API_BASE}/blogs/${blogId}/view`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (error) {
            console.error('Failed to increment blog views:', error);
        }
    }

    // Share blog on social media
    static async shareBlog(blogId, platform) {
        const response = await fetch(`${API_BASE}/blogs/share/${blogId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ platform }),
            credentials: 'include'
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || 'Failed to share blog');
        }
        return data;
    }
}